import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { CATEGORY_KIND_LABELS, categoryKindOrOther } from '../../lib/portalAssets/categoryKinds';
import type { PortalAssetClientCategory } from '../../types/portalAsset';

// THE CATEGORY PICKER (2026-10-10) — shared by the client page and the staff view,
// like the rest of AssetDetailView. An asset can be in SEVERAL categories.
//
// ⚠️⚠️ THE THREE STATES ARE DIFFERENT AND MUST READ DIFFERENTLY: the list is still
// loading, it FAILED, or it loaded and the brand genuinely has none. Collapsing them
// into one empty box is the defect this module keeps finding — a filter that matches
// nothing and a picker that offers nothing look identical when they are broken.
//
// ⚠️ What is saved is the set of category ids only. The kinds are DERIVED by the
// server, so there is nothing here to send and nothing a user could forge.

export type CategoriesState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; categories: PortalAssetClientCategory[] };

/** A react-query result → the picker's three states. ⚠️ `isPending` and `isError` are checked BEFORE `data`, so a failure can never read as "no categories". */
export function categoriesState(q: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data?: { categories: PortalAssetClientCategory[] };
}): CategoriesState {
  if (q.isError) return { status: 'error', message: q.error instanceof Error ? q.error.message : '' };
  if (q.isPending || !q.data) return { status: 'loading' };
  return { status: 'ready', categories: q.data.categories };
}

export function toggleId(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
}

export function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');
}

export function AssetCategoryPicker({
  audience,
  current,
  state,
  onSave,
  saving = false,
  saveError = null,
  draft: controlledDraft,
  onDraftChange,
  saveDisabled = false,
}: {
  /**
   * ⚠️ REQUIRED so every call site declares it. The category KIND is an internal matching key
   * (shared across brands), so only staff see it; a club sees the name its brand gave the
   * category and nothing else. Display only — what is saved and filtered on is unchanged.
   */
  audience: 'client' | 'staff';
  /** The asset's stored category ids. */
  current: readonly string[];
  state: CategoriesState;
  onSave: (ids: string[]) => void;
  saving?: boolean;
  saveError?: string | null;
  /**
   * CONTROLLED MODE (Run D): the host holds the draft so an AI suggestion can fill it and Accept can read it. Absent =
   * the picker owns its draft, exactly as before.
   */
  draft?: readonly string[];
  onDraftChange?: (ids: string[]) => void;
  /** The host is holding an unsaved AI suggestion: it is accepted as one, so this section's own Save waits. */
  saveDisabled?: boolean;
}) {
  const [ownDraft, setOwnDraft] = useState<string[]>([...current]);
  const draft: readonly string[] = controlledDraft ?? ownDraft;
  const setDraft = (f: (d: readonly string[]) => string[]) => {
    const next = f(draft);
    if (onDraftChange) onDraftChange(next);
    else setOwnDraft(next);
  };
  const unchanged = sameIdSet(draft, current);

  return (
    <form
      className="space-y-2"
      data-testid="asset-category-picker"
      onSubmit={(e) => {
        e.preventDefault();
        if (!unchanged) onSave([...draft]);
      }}
    >
      <fieldset>
        <legend className="font-medium text-foreground">Categories</legend>
        {state.status === 'loading' && <p className="text-xs text-muted-foreground">Loading categories…</p>}
        {state.status === 'error' && (
          <p className="text-xs text-foreground" role="alert">
            We couldn’t load the categories. {state.message}
          </p>
        )}
        {state.status === 'ready' && state.categories.length === 0 && (
          <p className="text-xs text-muted-foreground" data-testid="asset-no-categories">
            This brand has no categories set up yet, so this asset can’t be filed. Kyck can add some.
          </p>
        )}
        {state.status === 'ready' && state.categories.length > 0 && (
          // CSS multi-column, NOT a grid: categories carry a brand-set `order`, and columns flow
          // down-then-across so the sequence reads as one list split in half (a grid zigzags it).
          // One column on a phone (the client portal), two when there is room.
          <div className="columns-1 gap-x-6 sm:columns-2" data-testid="asset-category-columns">
        {state.categories.map((c) => (
            // ⚠️ break-inside-avoid: otherwise a checkbox can separate from its text at the column boundary. The vertical rhythm lives on the items (mb-1), not the container, or the columns lose it.
            <label key={c.id} className="mb-1 flex break-inside-avoid items-start gap-2 text-foreground">
              <input
                className="mt-1"
                type="checkbox"
                checked={draft.includes(c.id)}
                onChange={() => setDraft((d) => toggleId(d, c.id))}
              />
              <span>
                {c.name}
                {/* The kind (staff only) sits on its own line so it widens nothing: in a half-width column it was the thing that made a row wrap badly. */}
                {audience === 'staff' && (
                  <span className="block text-xs text-muted-foreground">{CATEGORY_KIND_LABELS[categoryKindOrOther(c.kind)]}</span>
                )}
                {c.description !== '' && (
                  <span className="block text-xs text-muted-foreground" data-testid="asset-category-description">{c.description}</span>
                )}
              </span>
            </label>
          ))}
          </div>
        )}
        <p className="mt-1 text-xs text-muted-foreground">An asset can be in more than one category.</p>
      </fieldset>
      {saveError && <p className="text-xs text-foreground" role="alert">{saveError}</p>}
      <Button type="submit" size="sm" disabled={saving || saveDisabled || unchanged || state.status !== 'ready'}>
        {saving ? 'Saving…' : 'Save categories'}
      </Button>
    </form>
  );
}
